/**
 * Store Locator Map Page Implementation
 * @description Handles the store locator map functionality, including initialization,
 * marker management, and location display
 */

/**
 * Calculate the distance between two points using the Haversine formula
 * @param {number} lat1 - Latitude of the first point
 * @param {number} lon1 - Longitude of the first point
 * @param {number} lat2 - Latitude of the second point
 * @param {number} lon2 - Longitude of the second point
 * @returns {string} Distance in kilometers or miles
 */
function calculateDistance(lat1, lon1, lat2, lon2, unit = 'km') {
  const R = unit === 'km' ? 6371 : 3958.8; // Radius of the Earth in km or miles
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return `${distance.toFixed(2)} ${unit}`;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

// Store locator SVG icons used throughout the component
const storeLocatorIcons = {
  phone: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Group 5680">
              <path id="Vector"
                d="M13.4382 1.42871H1.02527V7.79996H3.4338L6.33171 10.6979V7.79996H13.4382V1.42871Z"
                stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linejoin="bevel" />
              <path id="Vector_2"
                d="M9.10815 10.6982H16.975V14.7361H15.4485L13.612 16.5727V14.7361H9.10815V10.6982Z"
                stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linejoin="bevel" />
            </g>
          </svg>`,
  map: `<?xml version="1.0" encoding="UTF-8"?><svg id="Layer_2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23.5 21.5"><g id="Layer_1-2"><path d="M23.13.1c-.23-.13-.52-.13-.75,0l-6.65,3.8L8.09.08c-.1-.05-.22-.08-.34-.08-.05,0-.1.01-.15.02-.04,0-.08.01-.11.03-.06.02-.11.05-.16.08L.38,4.1c-.24.13-.38.38-.38.65v16c0,.27.14.51.37.65.23.13.52.13.75,0l6.65-3.8,7.56,3.77c.12.08.27.13.42.13.05,0,.1-.01.15-.02.04,0,.08-.01.11-.03.06-.02.11-.05.16-.08l6.95-3.97c.24-.13.38-.38.38-.65V.75c0-.27-.14-.51-.37-.65ZM1.5,5.19l5.5-3.15v14.28l-5.5,3.14V5.19ZM15,19.54l-6.5-3.25V1.96l6.5,3.25v14.33ZM22,16.31l-5.5,3.15V5.18l5.5-3.14v14.27Z"/></g></svg>`,
  mail: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
    <path d="M2.66732 3.16699H13.334C14.0673 3.16699 14.6673 3.76699 14.6673 4.50033V12.5003C14.6673 13.2337 14.0673 13.8337 13.334 13.8337H2.66732C1.93398 13.8337 1.33398 13.2337 1.33398 12.5003V4.50033C1.33398 3.76699 1.93398 3.16699 2.66732 3.16699Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M14.6673 4.5L8.00065 9.16667L1.33398 4.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
};

/**
 * Get store open status
 * @param {string} hours - Store hours in format "9.00-18.00" or "9.00-12.00&13.30-18.00"
 * @returns {Object} Object containing isOpen status and closingTime
 */

function parseStoreHours(hours) {
  if (!hours) return null;
  if (hours === 'closed' || hours === 'gesloten' || hours === 'fermé') return null;

  const periods = hours.split('&').map((period) => {
    const [start, end] = period.split('-');
    return { start: parseFloat(start), end: parseFloat(end) };
  });

  return periods;
}

function isWithinPeriod(currentTime, period) {
  return currentTime >= period.start && currentTime <= period.end;
}

function getStoreOpenStatus(hours) {
  if (!hours) return { isOpen: false, closingTime: null, openingTime: null };

  const periods = parseStoreHours(hours);
  if (!periods) return { isOpen: false, closingTime: null, openingTime: null };

  const d = new Date();
  const currentTime = parseFloat(d.getHours() + '.' + d.getMinutes());

  const activePeriod = periods.find((period) => isWithinPeriod(currentTime, period));

  // Get opening time for closed stores
  let openingTime = null;
  if (!activePeriod) {
    // Find the next opening time
    const nextPeriod = periods.find((period) => currentTime < period.start);
    if (nextPeriod) {
      openingTime = nextPeriod.start;
    } else {
      // If no next period today, get the first period of the day
      openingTime = periods[0].start;
    }
  }

  // Format closing time to ensure it has a trailing zero for x.3 times
  const formatClosingTime = (time) => {
    const [hours, minutes] = time.toString().split('.');
    return `${hours}.${(minutes || '0').padEnd(2, '0')}`;
  };

  return {
    isOpen: !!activePeriod,
    closingTime: activePeriod ? formatClosingTime(activePeriod.end) : null,
    openingTime: openingTime ? formatClosingTime(openingTime) : null,
  };
}

function createMarkerIcon(isActive) {
  const svg = `<svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g id="Group 5772">
        ${
          isActive
            ? `
            <circle id="Ellipse 275" opacity="0.2" cx="22" cy="22" r="22" fill="#F05323"/>
            <circle id="Ellipse 274" opacity="0.5" cx="22" cy="22" r="13" fill="#F05323"/>
          `
            : ``
        }
        <circle id="Ellipse 233" cx="22" cy="22" r="6" fill="#F05323"/>
      </g>
    </svg>`;

  return {
    url: 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(svg),
    anchor: new google.maps.Point(22, 22),
    scaledSize: new google.maps.Size(44, 44),
  };
}

function generateBusinessDetails(location) {
  if (!location) return '';

  const addressHtml =
    location.address && location.address.address1
      ? `
    <li class="my-location-result__address">
      <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        location.name,
      )}&query_place_id=${location.address.latitude},${location.address.longitude}" target="_blank">
        ${location.address.address1}${location.address.zip ? `, ${location.address.zip}` : ''} ${
          location.address.city
        }
      </a>
    </li>
  `
      : '';

  const iteneraryHtml =
    location.address && location.address.address1
      ? `
    <a class="my-location-result__link-route link-underline-before" href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      location.name,
    )}&query_place_id=${location.address.latitude},${location.address.longitude}" target="_blank">
     <span>${storeLocator.Data.itenerary || "Planificateur d'itinéraire"}</span>
    </a>
  `
      : '';

  const phoneHtml =
    location.phoneOverride?.value || location.address?.phone
      ? `
    <li class="my-location-result__address">
      <div class="icon icon-phone">${storeLocatorIcons.phone}</div>
      <a class="my-location-result__address-link" href="tel:${
        location.phoneOverride?.value || location.address?.phone
      }">
        ${location.phoneOverride?.value || location.address?.phone}
      </a>
    </li>
  `
      : '';

  const emailValue =
    location.email?.value ||
    location.store?.reference?.fields?.find((f) => f.key === 'store_email')?.value;
  const emailHtml = emailValue
    ? `
    <li class="my-location-result__address">
      <div class="icon icon-mail">${storeLocatorIcons.mail}</div>
      <a class="my-location-result__address-link" href="mailto:${emailValue}">${emailValue}</a>
    </li>
  `
    : '';

  const daysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const today = new Date().getDay();
  const d = new Date();
  const time = parseFloat(`${d.getHours()}.${String(d.getMinutes()).padStart(2, '0')}`);
  const todayDate = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(
    2,
    '0',
  )}/${String(d.getFullYear()).slice(-2)}`;

  // Retrieve exceptional opening hours from store reference fields
  const exceptionalOpensField = location.store?.reference?.fields?.find(
    (f) => f.key === 'exceptionally_opens',
  )?.value;

  // Directly access the handle property
  const storeLink = location.store?.reference?.handle || null;

  let exceptionalOpens = [];
  if (exceptionalOpensField) {
    try {
      exceptionalOpens = JSON.parse(exceptionalOpensField);
    } catch (e) {
      console.error('Error parsing exceptionalOpens:', e);
    }
  }

  // Check for exceptional opening hours
  let isExceptionallyOpen = false;
  let exceptionalClosingTime = null;

  exceptionalOpens.forEach((exceptionalOpen) => {
    // Handle both formats: "date | time" and "date|time"
    const parts = exceptionalOpen.includes(' | ')
      ? exceptionalOpen.split(' | ')
      : exceptionalOpen.split('|').map((s) => s.trim());

    const [date, period] = parts;

    // Clean the date string and handle different formats
    let cleanDate = date.replace(/\s+/g, '').replace(/[\[\]"]/g, '');

    // If the date contains day names or month names, extract just the DD/MM/YY part
    if (cleanDate.includes('/')) {
      // Extract the date part that matches DD/MM/YY format
      const dateMatch = cleanDate.match(/(\d{1,2}\/\d{1,2}\/\d{2})/);
      if (dateMatch) {
        cleanDate = dateMatch[1];
      }
    }

    if (cleanDate === todayDate) {
      // Handle both formats: "start - end" and "start-end"
      const timeParts = period.includes(' - ')
        ? period.split(' - ')
        : period.split('-').map((s) => s.trim());

      const [start, end] = timeParts;
      const startTime = parseFloat(start.replace(':', '.'));
      const endTime = parseFloat(end.replace(':', '.'));

      if (time >= startTime && time <= endTime) {
        isExceptionallyOpen = true;
        exceptionalClosingTime = end;
      }
    }
  });

  let hoursHtml = '';
  if (isExceptionallyOpen) {
    hoursHtml = `
      <li class="my-location-result__address store--open">
        <span class="store-status-indicator"></span>
        <span>
          ${storeLocator.Data.nowExceptionallyOpen || 'Now Exceptionally Open'} ${
      storeLocator.Data.untilText || 'until'
    } ${exceptionalClosingTime}
        </span>
      </li>
    `;
  } else {
    const storeFields = location.store?.reference?.fields || [];
    const todayHours =
      storeFields.find((f) => f.key === `opening_hours_${daysMap[today]}`)?.value ||
      location[`opening_hours_${daysMap[today]}`];

    if (todayHours) {
      const formattedHours = todayHours.replace(/\s/g, '').replace(/:/g, '.');
      const { isOpen, closingTime, openingTime } = getStoreOpenStatus(formattedHours);

      hoursHtml = `
        <li class="my-location-result__address ${isOpen ? 'store--open' : 'store--closed'}">
          <span class="store-status-indicator"></span>
          <span>
            ${
              isOpen
                ? `${storeLocator.Data.nowOpen || 'Now Open'} ${
                    storeLocator.Data.untilText || 'until'
                  } ${closingTime}`
                : formattedHours === 'closed' ||
                  formattedHours === 'gesloten' ||
                  formattedHours === 'fermé'
                ? `${storeLocator.Data.nowClosed || 'Now Closed'}`
                : `${storeLocator.Data.nowClosed || 'Now Closed'} – ${
                    storeLocator.Data.opensAt || 'opens at'
                  } ${openingTime}`
            }
          </span>
        </li>
      `;
    }
  }

  return `
    <div class="my-location-result__callout">
      <div class="my-location-result__details">
        <div class="my-location-result__name">${location.name}</div>
      </div>
      <ul class="list-unstyled my-location-result__business">
        ${addressHtml}
        ${phoneHtml}
        ${emailHtml}
        ${hoursHtml}
      </ul>
      <div class="button-group button-group--align-center">
        ${
          storeLink
            ? `<a href="${
                window.location.pathname.includes('/fr') ? '/fr' : ''
              }/pages/store/${storeLink}" class="button button--secondary-white">
          <span data-button-animate-chars="" class="button__text">
            ${storeLocator.Data.viewStorePage || 'View Store Page'}
          </span>
        </a>`
            : ''
        }

        ${iteneraryHtml}
      </div>
    </div>
  `;
}

function fitMapBounds(map, locations, padding = 50) {
  if (!locations?.length) return;

  const bounds = new google.maps.LatLngBounds();
  let hasValidLocations = false;

  locations.forEach((location) => {
    if (location.address?.latitude && location.address?.longitude) {
      const lat = parseFloat(location.address.latitude);
      const lng = parseFloat(location.address.longitude);

      if (!isNaN(lat) && !isNaN(lng)) {
        bounds.extend(new google.maps.LatLng(lat, lng));
        hasValidLocations = true;
      }
    }
  });

  if (hasValidLocations) {
    map.fitBounds(bounds, {
      padding: {
        top: padding,
        right: padding,
        bottom: padding,
        left: padding,
      },
    });

    // Prevent excessive zoom
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const zoom = map.getZoom();
      if (zoom > 15) map.setZoom(15);
      else if (zoom < 7) map.setZoom(7);
    });
  }
}

/**
 * Initialize Store Detail Map
 * @description Initializes a map for a single store location
 * @param {HTMLElement} mapElement - The map container element
 */
async function initStoreDetailMap(mapElement) {
  if (!mapElement) return;

  // Check for Google Maps API key
  const googleToken = window.storeLocator?.Data?.googleMapsToken;
  if (!googleToken) {
    console.error('Google Maps API key not found');
    return;
  }

  try {
    const { Map } = await google.maps.importLibrary('maps');
    const { mapOptions } = await import('./map-config.js');

    const latitude = parseFloat(mapElement.dataset.latitude);
    const longitude = parseFloat(mapElement.dataset.longitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      console.error('Invalid latitude or longitude');
      return;
    }

    const storeLocation = { lat: latitude, lng: longitude };

    const map = new Map(mapElement, {
      ...mapOptions,
      center: storeLocation,
      zoom: 16,
      scrollwheel: false,
    });

    // Create and add the store marker
    new google.maps.Marker({
      position: storeLocation,
      map: map,
      icon: createMarkerIcon(true),
    });
  } catch (error) {
    console.error('Error initializing store detail map:', error);
  }
}

function mapPage() {
  /**
   * Configuration object for DOM elements and classes
   */
  const config = {
    dom: {
      map: 'map',
      desktopMapWrapper: 'store-locator-desktop-wrapper',
      mobileMapWrapper: 'mobileMapWrapper',
      search: 'search',
      results: 'myLocationResults',
      result: '.js-my-location-result',
      result_button: '.js-make-my-store-btn',
      active_result: '.js-my-location-result:first-child',
      acc_button: '.js-acc-button',
      acc_icon: '.js-acc-icon',
      acc_details: '.js-acc-details',
      result_link: '.js-my-location-link',
      result_details: '.js-my-results-details',
    },
    cls: {
      active: 'is-active',
    },
  };

  // Core variables for map functionality
  const googleToken =
    (storeLocator && storeLocator.Data && storeLocator.Data.googleMapsToken) || null;
  const $map = document.getElementById(config.dom.map) || null;
  const $results = document.getElementById(config.dom.results);
  const googleMarkers = [];
  let map = null;

  // Initialize the map page functionality
  async function init() {
    // Wait for Google Maps API to be ready
    if (typeof google === 'undefined') {
      console.error('Google Maps API not loaded');
      return;
    }

    // Check for store detail map
    const storeDetailMap = document.querySelector('.js-store-detail-map');
    if (storeDetailMap) {
      initStoreDetailMap(storeDetailMap);
      return; // Exit early since this is a store detail page
    }

    // First check for preferred store in localStorage
    if (storageAvailable('localStorage')) {
      const storedPreferredId = localStorage.getItem('preferredStoreId');
      if (storedPreferredId) {
        storeLocator.preferredStoreId = storedPreferredId;
      }
    }

    // Check if locations are already loaded in the API response
    const fashionSocietyLocations = await getFashionSocietyLocations();
    if (fashionSocietyLocations && fashionSocietyLocations.locations) {
      storeLocator.allLocationsData = fashionSocietyLocations.locations;
    }

    if (googleToken && $map) {
      initGoogleMaps();
    }

    if ($results) {
      checkIfMapShouldBeMoved();
      addEvents();
    }
  }

  /**
   * Check if browser storage is available
   * @param {string} type - Storage type to check ('localStorage' or 'sessionStorage')
   * @returns {boolean} Whether storage is available
   */
  function storageAvailable(type) {
    let storage;
    try {
      storage = window[type];
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get locations from Fashion Society API
   */
  async function getFashionSocietyLocations() {
    try {
      let locationsApi = `${theme.routes.apiUrl}/locations?unfiltered=true`;
      if (window.location.hostname.includes('development')) {
        locationsApi += `?shop=${window.Shopify.shop}`;
      }

      const response = await fetch(locationsApi);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching Fashion Society locations:', error);
      return null;
    }
  }

  // Initialize Google Maps
  function initGoogleMaps() {
    async function initMap() {
      const { Map } = await google.maps.importLibrary('maps');
      const { mapOptions } = await import('./map-config.js');

      // Set initial center to Belgium
      const belgiumCenter = { lat: 50.8503, lng: 4.3517 };
      const initialMapOptions = {
        ...mapOptions,
        center: belgiumCenter,
        zoom: 8,
      };

      map = new Map(document.getElementById('map'), initialMapOptions);

      // Get locations from storeLocator
      const allLocations = storeLocator?.allLocationsData || [];

      // First add all markers to the map
      allLocations.forEach((location) => {
        if (location.address?.latitude && location.address?.longitude) {
          addPin({
            dataset: {
              id: location.id,
              latitude: location.address.latitude,
              longitude: location.address.longitude,
              name: location.name,
              active: (location.id === storeLocator?.preferredStoreId).toString(),
            },
          });
        }
      });

      // Then handle the bounds for filtered locations if there's a preferred store
      if (storeLocator.preferredStoreId) {
        const filteredLocations = allLocations.filter((location) => {
          // Add your filtering logic here - e.g., within 50km of preferred store
          return true; // For now, return all locations
        });

        if (filteredLocations.length > 0) {
          fitMapBounds(map, filteredLocations);
        }
      } else {
        // If no preferred store, fit bounds to show all locations
        fitMapBounds(map, allLocations);
      }

      // Prevent excessive zoom
      google.maps.event.addListenerOnce(map, 'idle', function () {
        const currentZoom = map.getZoom();
        if (currentZoom > 15) {
          map.setZoom(15);
        } else if (currentZoom < 7) {
          map.setZoom(7);
        }
      });

      // Make showLocation available globally
      window.showLocation = showLocation;
    }

    initMap();
  }

  /**
   * Show Location on Map
   * @description Updates the map view to show either a single location or multiple filtered locations
   * @param {HTMLElement} $location - DOM element containing location data
   * @param {Array} filteredLocations - Array of filtered location objects to display
   */
  function showLocation($location, filteredLocations = null) {
    // Keep all markers visible
    googleMarkers.forEach((markerData) => {
      markerData.marker.setMap(map);
      // Reset z-index to default
      markerData.marker.setZIndex(1);
      // Close any open info windows
      if (markerData.infoWindow) {
        markerData.infoWindow.close();
      }
    });

    if (filteredLocations) {
      // Create bounds object to fit filtered locations
      const bounds = new google.maps.LatLngBounds();
      let hasValidLocations = false;

      // Only adjust bounds to show filtered locations
      filteredLocations.forEach((location) => {
        if (location.address?.latitude && location.address?.longitude) {
          const lat = parseFloat(location.address.latitude);
          const lng = parseFloat(location.address.longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            bounds.extend(new google.maps.LatLng(lat, lng));
            hasValidLocations = true;

            // Highlight filtered markers with higher z-index
            const marker = googleMarkers.find((m) => m.id === location.id);
            if (marker) {
              marker.marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
            }
          }
        }
      });

      // Only fit bounds if we have filtered locations
      if (hasValidLocations) {
        fitMapBounds(map, filteredLocations);

        // Prevent excessive zoom
        google.maps.event.addListenerOnce(map, 'idle', function () {
          const currentZoom = map.getZoom();
          if (currentZoom > 15) {
            map.setZoom(15);
          } else if (currentZoom < 7) {
            map.setZoom(7);
          }
        });
      }
    } else if ($location) {
      // Single location handling
      const { latitude, longitude, id } = $location.dataset || {};

      if (latitude && longitude) {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);

        if (!isNaN(lat) && !isNaN(lng)) {
          const storeLocation = new google.maps.LatLng(lat, lng);
          map.panTo(storeLocation);
          map.setZoom(15);

          // Find and open the info window for this location
          const markerData = googleMarkers.find((marker) => marker.id === id);
          if (markerData) {
            markerData.marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
            markerData.infoWindow.open(map, markerData.marker);
          }
        }
      }
    }
  }

  /**
   * Add Event Listeners
   * @description Sets up all necessary event listeners for the store locator:
   * - Mutation observer for dynamically added results
   * - Accordion functionality for location details
   * - Window resize handler for responsive map placement
   */
  function addEvents() {
    /**
     * Helper functions for accordion functionality
     */
    function openAcc(el) {
      el.setAttribute('aria-expanded', true);
      el.classList.add(config.cls.active);
    }

    function closeAcc(el) {
      el.setAttribute('aria-expanded', false);
      el.classList.remove(config.cls.active);
    }

    // Set up mutation observer to handle dynamically added results
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation?.type === 'childList') {
          const $resultLocations = document.querySelectorAll(config.dom.result) || null;
          // const $activeResult = document.querySelector(config.dom.active_result) || null;

          if (mutation?.addedNodes?.length <= 0) {
            return;
          }

          if ($resultLocations?.length > 0) {
            [...$resultLocations].forEach(($resultLocation) => {
              // add events to the result locations
              const hasEvents = $resultLocation.getAttribute('data-events-added') || false;
              if (hasEvents) return;

              addPin($resultLocation);
              $resultLocation.setAttribute('data-events-added', true);
              const $accButton = $resultLocation.querySelector(config.dom.acc_button) || null;
              const $accIcon = $resultLocation.querySelector(config.dom.acc_icon) || null;
              const $accDetails = $resultLocation.querySelector(config.dom.acc_details) || null;

              $resultLocation.addEventListener('click', (e) => {
                const $target = e.currentTarget || null;
                showLocation($target);
              });

              if ($accButton && $accDetails) {
                $accButton.addEventListener('click', () => {
                  if ($accDetails.classList.contains(config.cls.active)) {
                    closeAcc($accDetails);
                    if ($accIcon) closeAcc($accIcon);
                  } else {
                    openAcc($accDetails);
                    if ($accIcon) openAcc($accIcon);
                  }

                  $accButton.setAttribute('data-acc', true);
                });
              }
            });
          }

          // if ($activeResult) {
          //   showLocation($activeResult);
          // }
        }
      });
    });

    observer.observe($results, { childList: true });

    // move the map to the correct wrapper on resize
    window.addEventListener('resize', () => {
      checkIfMapShouldBeMoved();
    });
  }

  /**
   * Check Map Position
   * @description Determines if the map should be in the mobile or desktop wrapper
   * based on the current viewport width
   */
  function checkIfMapShouldBeMoved() {
    const width =
      window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const $desktopMapWrapper = document.getElementById(config.dom.desktopMapWrapper) || null;
    const $mobileMapWrapper = document.getElementById(config.dom.mobileMapWrapper) || null;
    const $map = document.getElementById(config.dom.map);
    if (!$map) return;

    if (width <= 1020) {
      $mobileMapWrapper.appendChild($map);
    } else {
      $desktopMapWrapper.appendChild($map);
    }
  }

  /**
   * Add Map Pin
   * @description Creates and adds a new map marker for a store location
   * @param {HTMLElement|Object} $result - DOM element or object containing the location data
   * @returns {void}
   */
  function addPin($result) {
    if (!$result) return;

    // Handle both DOM elements and plain objects
    const isDOM = $result instanceof Element;
    const dataset = isDOM ? $result.dataset : $result.dataset;

    if (!dataset || !map) return;

    const { id, latitude: latitudeString, longitude: longitudeString, name, active } = dataset;
    const latitude = parseFloat(latitudeString);
    const longitude = parseFloat(longitudeString);

    // Check if marker already exists
    const existingMarker = googleMarkers.find((m) => m.id === id);
    if (existingMarker) {
      // Update existing marker if needed
      return;
    }

    // Get details from DOM element if available
    let businessDetailsCallout = null;
    if (isDOM) {
      const $details = $result.querySelector(config.dom.result_details);
      const $link = $details?.querySelector(config.dom.result_link);
      if ($details) {
        businessDetailsCallout = `
          <div class="my-location-result__callout">
            ${$details.innerHTML}
            ${$link ? $link.innerHTML : ''}
          </div>
        `;
      }
    } else {
      // Create business details for non-DOM locations
      const location = storeLocator.allLocationsData.find((loc) => loc.id === id);
      if (location) {
        businessDetailsCallout = generateBusinessDetails(location);
      }
    }

    // Check if this is the active/preferred store
    const isActive = id === storeLocator?.preferredStoreId;

    const svg = createMarkerIcon(isActive);

    const marker = new google.maps.Marker({
      position: { lat: latitude, lng: longitude },
      map,
      title: name,
      icon: svg,
      zIndex: isActive ? google.maps.Marker.MAX_ZINDEX + 1 : 1,
    });

    const infoWindow = new google.maps.InfoWindow({
      content: businessDetailsCallout || name,
      maxWidth: 300,
    });

    marker.addListener('click', () => {
      // Close all other info windows
      googleMarkers.forEach((m) => {
        if (m.infoWindow) {
          m.infoWindow.close();
        }
      });
      infoWindow.open(map, marker);
    });

    googleMarkers.push({
      infoWindow,
      marker,
      id,
    });
  }

  return init();
}

// Initialize the map page functionality after the DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mapPage);
} else {
  mapPage();
}
